import { SOUND_DEFINITIONS, type SoundDefinition } from "./data/sounds.ts";

export interface SoundInit {
  id: string;
  name: string;
  offset: number;
  duration: number;
  colorClass: string;
}

export class Sound {
  readonly id: string;
  readonly name: string;
  readonly offset: number;
  readonly duration: number;
  readonly colorClass: string;

  constructor(init: SoundInit) {
    this.id = init.id;
    this.name = init.name;
    this.offset = init.offset;
    this.duration = init.duration;
    this.colorClass = init.colorClass;
  }

  static readonly all: readonly Sound[] = SOUND_DEFINITIONS.map(
    (def: SoundDefinition) => new Sound(def),
  );

  static readonly MAP: Readonly<Record<string, Sound>> = Object.fromEntries(
    Sound.all.map((s) => [s.id, s]),
  );

  static get DEFAULT(): Sound {
    return Sound.MAP["kick"] ?? Sound.all[0]!;
  }

  static findById(id?: string): Sound | undefined {
    if (!id) return undefined;
    return Sound.MAP[id];
  }

  static findByName(name?: string): Sound | undefined {
    if (!name) return undefined;
    return Sound.all.find((s) => s.name.toLowerCase() === name.toLowerCase());
  }

  static next(sound: Sound | undefined): Sound {
    if (!sound) {
      return Sound.all[0] ?? Sound.DEFAULT;
    }
    const idx = Sound.all.findIndex((s) => s.id === sound.id);
    if (idx === -1) {
      return Sound.all[0] ?? Sound.DEFAULT;
    }
    return Sound.all[(idx + 1) % Sound.all.length] ?? Sound.DEFAULT;
  }

  static previous(sound: Sound | undefined): Sound {
    if (!sound) {
      return Sound.all[Sound.all.length - 1] ?? Sound.DEFAULT;
    }
    const idx = Sound.all.findIndex((s) => s.id === sound.id);
    if (idx === -1 || idx === 0) {
      return Sound.all[Sound.all.length - 1] ?? Sound.DEFAULT;
    }
    return Sound.all[(idx - 1) % Sound.all.length] ?? Sound.DEFAULT;
  }
}

export default Sound;
